/**
 * Hooks for the Video domain.
 *
 * Replaces Wized requests: get_videos, get_video_index
 * Replaces Wized elements: course_video_item, course_video_name, course_video_index,
 *   vid_ind_list, vid_ind_name, vid_indx, course_video (iframe src), etc.
 */

import { useMemo } from "react";
import { useAsync } from "./useAsync";
import * as api from "../lib/api";
import { filterPurchasedCourseVideos } from "../lib/videoFilters";
import type { VideoRow, VideoIndexRow } from "../lib/schema";

/** Fetch all videos. */
export function useVideos() {
  return useAsync(() => api.getVideos(), []);
}

/** Fetch videos for a single purchased course, excluding public previews. */
export function useCourseVideoDirect(courseId: string | null) {
  return useAsync(
    async () => {
      const result = await api.getCourseVideos(courseId!);
      return { ...result, data: filterPurchasedCourseVideos(result.data) };
    },
    courseId ? [courseId] : null,
  );
}

/** Fetch video index for a single course. */
export function useCourseVideoIndex(courseId: string | null) {
  return useAsync(
    () => api.getCourseVideoIndex(courseId!),
    courseId ? [courseId] : null,
  );
}

/** Fetch all video index/timestamp entries. */
export function useVideoIndex() {
  const result = useAsync<VideoIndexRow[]>(async () => {
    const result = await api.getVideoIndex();
    return { data: result.data as VideoIndexRow[] | null, error: result.error };
  }, []);

  // Sort by id (ascending) to match the legacy behavior
  const sortedData = useMemo(() => {
    if (!result.data) return null;
    return [...result.data].sort((a, b) => a.id - b.id);
  }, [result.data]);

  return { ...result, data: sortedData };
}

/** Filter videos for a specific purchased course, excluding public previews. */
export function useCourseVideos(
  videos: VideoRow[] | null,
  courseId: string | null,
) {
  return useMemo(() => {
    if (!videos || !courseId) return [];
    return filterPurchasedCourseVideos(videos.filter((v) => v.course_id === courseId)) ?? [];
  }, [videos, courseId]);
}

/** Get video timestamps for a specific video. */
export function useVideoTimestamps(
  videoIndex: VideoIndexRow[] | null,
  videoId: string | null,
) {
  return useMemo(() => {
    if (!videoIndex || !videoId) return [];
    return videoIndex.filter((vi) => vi.video_id === videoId);
  }, [videoIndex, videoId]);
}
